import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Printer, QrCode, Search, Home, Users, Baby } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Logo } from "../components/Logo";
import { TransferAccountsCard } from "../components/TransferAccountsCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, apiError, money, fileUrl } from "../lib/api";

export const StatusBadge = ({ status, type = "registration" }) => {
  const labels = {
    registration: { pre_registered: "Pre-registro recibido", confirmed: "Confirmado", cancelled: "Cancelado", active: "Activo" },
    payment: { pending: "Pendiente de pago", receipt_uploaded: "Comprobante en revisión", approved: "Pago aprobado", rejected: "Pago rechazado", correction_requested: "Corrección solicitada", waived: "Cortesía", imported_confirmed: "Pago confirmado (importado)" },
  };
  const colors = {
    pre_registered: "bg-hz-cyan/10 text-hz-blue",
    confirmed: "bg-hz-success/10 text-hz-success",
    active: "bg-hz-success/10 text-hz-success",
    cancelled: "bg-hz-error/10 text-hz-error",
    pending: "bg-hz-warning/10 text-hz-warning",
    receipt_uploaded: "bg-hz-cyan/10 text-hz-blue",
    approved: "bg-hz-success/10 text-hz-success",
    rejected: "bg-hz-error/10 text-hz-error",
    correction_requested: "bg-hz-warning/10 text-hz-warning",
    waived: "bg-hz-success/10 text-hz-success",
    imported_confirmed: "bg-hz-success/10 text-hz-success",
  };
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${colors[status] || "bg-hz-gray/30 text-hz-ink"}`} data-testid={`status-${type}`}>
      {labels[type]?.[status] || status}
    </span>
  );
};

export const RegistrationSummary = ({ data, showPhoto = true }) => (
  <div className="space-y-6">
    <div className="flex items-start gap-4 rounded-xl border border-hz-gray/40 bg-hz-bg p-4" data-testid="summary-attendee-info">
      {showPhoto && data.photo_url && (
        <img src={fileUrl(data.photo_url)} alt={`Fotografía de ${data.full_name}`} className="h-20 w-20 rounded-xl border border-hz-gray/50 object-cover" data-testid="attendee-photo" />
      )}
      <div className="text-sm">
        <p className="text-base font-bold text-hz-ink" data-testid="attendee-name">{data.full_name}</p>
        <p className="mt-0.5 font-semibold text-hz-navy">
          {data.delegation_name} · {data.sector_name}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <StatusBadge status={data.registration_status} type="registration" />
          <StatusBadge status={data.payment_status} type="payment" />
        </div>
      </div>
    </div>

    <div className="rounded-xl border border-hz-gray/40" data-testid="summary-accesses">
      <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Accesos solicitados</p>
      <div className="divide-y divide-hz-gray/30">
        {data.accesses.map((a, i) => {
          const isWaitlist = a.type === "dinner" && data.dinner_reservation === "waitlisted";
          const st = a.status || "pending";
          let label = "Solicitado";
          let tone = "text-hz-cyan";
          if (isWaitlist) { label = "En lista de espera"; tone = "text-hz-warning"; }
          else if (st === "active") { label = "Aprobado"; tone = "text-hz-success"; }
          else if (st === "revoked") { label = "Revocado"; tone = "text-hz-error"; }
          return (
            <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-hz-ink">{a.name}</span>
              <span className={`text-xs font-bold ${tone}`}
                data-testid={a.type === "dinner" ? "summary-dinner-status" : `summary-${a.type}-status`}>
                {label}
              </span>
            </div>
          );
        })}
        {data.dinner_reservation === "waitlisted" && (
          <p className="mx-4 mb-3 rounded-lg border border-hz-warning/40 bg-hz-warning/10 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="dinner-waitlist-note">
            Tu solicitud de cena especial está en lista de espera: no garantiza el acceso ni genera tu pase de cena, y no se incluye en tu total hasta que sea confirmada.
          </p>
        )}
        {data.workshops.map((w) => {
          const st = w.status || "pending";
          let label = "Solicitado";
          let tone = "text-hz-cyan";
          if (st === "active") { label = "Aprobado"; tone = "text-hz-success"; }
          else if (st === "revoked") { label = "Revocado"; tone = "text-hz-error"; }
          return (
            <div key={w.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="text-hz-ink">Taller: {w.name}</span>
              <span className={`text-xs font-bold ${tone}`} data-testid={`summary-workshop-${w.id}-status`}>{label}</span>
            </div>
          );
        })}
        <div className="flex items-center justify-between bg-hz-navy/5 px-4 py-3">
          <span className="text-sm font-bold text-hz-navy">Total a pagar</span>
          <span className="text-base font-extrabold text-hz-navy" data-testid="summary-total-estimated">
            {data.total_estimated != null ? `${money(data.total_estimated)} MXN` : "Se compartirá próximamente"}
          </span>
        </div>

    {data.children?.length > 0 && (
      <div className="rounded-xl border border-hz-gray/40 px-4 py-3 text-sm" data-testid="summary-children">
        <p className="font-bold text-hz-navy"><Baby className="mr-1.5 inline h-4 w-4" /> Niños ({data.children.length})</p>
        <ul className="mt-1.5 space-y-1">
          {data.children.map((c) => (
            <li key={c.id} className="text-hz-ink/80" data-testid={`summary-child-${c.id}`}>
              {c.full_name}{c.care_required ? " · Cuidado infantil solicitado" : ""}{c.photo_status === "pending" ? " · Fotografía pendiente" : ""}
            </li>
          ))}
        </ul>
      </div>
    )}
      </div>
    </div>
  </div>
);

export const GroupSummary = ({ data, notice }) => (
  <div className="space-y-6" data-testid="group-summary">
    {notice && (
      <p className="rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="group-payment-rule-notice">{notice}</p>
    )}
    <div className="rounded-xl border border-hz-gray/40 bg-hz-bg p-4 text-sm" data-testid="group-contact-info">
      <p className="font-bold text-hz-navy">{data.group_name || data.group_type_label} · {data.group_type_label}</p>
      <p className="text-hz-ink/70">Contacto: {data.primary_contact_name}{data.primary_contact_email ? ` · ${data.primary_contact_email}` : ""}{data.primary_contact_phone ? ` · ${data.primary_contact_phone}` : ""}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <StatusBadge status={data.payment_mode} type="payment" />
        <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-bold text-hz-navy">{data.total_adults} adulto(s) · {data.total_children} niño(s)</span>
      </div>
    </div>

    <div className="rounded-xl border border-hz-gray/40" data-testid="group-members-list">
      <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy"><Users className="mr-1.5 inline h-4 w-4" /> Adultos del registro</p>
      <div className="divide-y divide-hz-gray/30">
        {data.members.map((m) => (
          <div key={m.id} className="px-4 py-3" data-testid={`group-member-${m.folio}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {m.photo_url && <img src={fileUrl(m.photo_url)} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                <div>
                  <p className="text-sm font-bold text-hz-ink">{m.full_name}</p>
                  <p className="text-xs font-extrabold tracking-wider text-hz-navy">{m.folio}</p>
                </div>
              </div>
              <StatusBadge status={m.payment_status} type="payment" />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 pl-13">
              {m.accesses.map((a, i) => (
                <span key={i} className="rounded-full bg-hz-cyan/10 px-2.5 py-0.5 text-xs font-semibold text-hz-blue">{a.name}</span>
              ))}
              {m.workshops.map((w) => (
                <span key={w.id} className="rounded-full bg-hz-turquoise/15 px-2.5 py-0.5 text-xs font-semibold text-hz-navy">{w.name}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between bg-hz-navy/5 px-4 py-3">
        <span className="text-sm font-bold text-hz-navy">Total a pagar</span>
        <span className="text-base font-extrabold text-hz-navy" data-testid="group-total">
          {data.total_amount != null ? `${money(data.total_amount)} MXN` : "Se compartirá próximamente"}
        </span>
      </div>
    </div>

    {data.children.length > 0 && (
      <div className="rounded-xl border border-hz-gray/40 px-4 py-3 text-sm" data-testid="group-children-info">
        <p className="font-bold text-hz-navy"><Baby className="mr-1.5 inline h-4 w-4" /> {data.children.length} niño(s) registrado(s) para cuidado infantil</p>
        <ul className="mt-2 space-y-1.5">
          {data.children.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`group-child-${c.id}`}>
              <span className="font-semibold text-hz-ink">{c.full_name}</span>
              {c.care_required && (
                <span className="rounded-full bg-hz-navy/5 px-2 py-0.5 font-bold text-hz-navy" data-testid={`group-child-fee-${c.id}`}>
                  Cuidado infantil
                </span>
              )}
              {c.child_care_eligible ? (
                <span className="rounded-full bg-hz-success/10 px-2 py-0.5 font-bold text-hz-success">Elegible para cuidado</span>
              ) : (
                <span className="rounded-full bg-hz-warning/10 px-2 py-0.5 font-bold text-hz-warning">Fotografía pendiente: acceso a cuidado infantil bloqueado</span>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-hz-ink/60">El QR familiar de cuidado infantil estará disponible en «Consultar mi registro» una vez confirmado el pago.</p>
        {data.children.some((c) => !c.child_care_eligible) && (
          <p className="mt-2 rounded-lg bg-hz-warning/5 px-3 py-2 text-xs font-medium text-hz-ink" data-testid="group-photo-pending-instructions">
            Para completar la fotografía de un menor, acude al módulo de cuidado infantil el día del evento o contacta al equipo organizador; un responsable autorizado la cargará y validará.
          </p>
        )}
      </div>
    )}
  </div>
);

const ReceiptsReceivedList = ({ receipts }) => {
  if (!receipts || receipts.length === 0) return null;
  return (
    <div className="mt-3 rounded-lg border border-hz-gray/40 bg-white" data-testid="receipts-received-list">
      <p className="border-b border-hz-gray/40 px-3 py-2 text-xs font-bold text-hz-navy">Comprobantes recibidos ({receipts.length})</p>
      <ul className="divide-y divide-hz-gray/30 text-xs">
        {receipts.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`receipt-received-${r.version_number}`}>
            <div className="min-w-0">
              <p className="truncate font-semibold text-hz-ink" title={r.original_filename}>#{r.version_number} · {r.original_filename}</p>
              <p className="text-[11px] text-hz-ink/60">{new Date(r.uploaded_at).toLocaleString("es-MX")}</p>
            </div>
            {r.download_url && (
              <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-bold text-hz-blue hover:underline" data-testid={`receipt-view-${r.version_number}`}>Ver</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ConfirmacionPage = () => {
  const { folio } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const isGroup = /^(HZ26)-(GRP|FAM)-/i.test(folio || "");
  const isFamily = (folio || "").toUpperCase().startsWith("HZ26-FAM");

  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);
  const [verify, setVerify] = useState({ email: "", phone: "" });
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const sessionAuth = (() => {
    try {
      const s = JSON.parse(sessionStorage.getItem("hz_last_reg") || "null");
      if (s && s.folio === (isGroup ? data?.group_folio : data?.folio)) return s;
    } catch { /* sin sesión */ }
    return null;
  })();
  const uploadEmail = sessionAuth?.email || verify.email.trim();
  const uploadPhone = sessionAuth?.phone || verify.phone.trim();
  const canAttemptUpload = !!(uploadEmail || uploadPhone);
  const receivedReceipts = data?.payment?.receipts || [];
  const maxReceipts = data?.payment?.max_receipts || 10;
  const remainingSlots = Math.max(0, maxReceipts - receivedReceipts.length - queue.length);
  const alreadyInReview = data?.payment?.status === "receipt_uploaded";
  // Fase 2C — Carga posterior: aunque el pago figure aprobado/cortesía, si queda saldo pendiente
  // (p. ej. aprobación parcial por acceso), el usuario puede seguir subiendo comprobantes.
  const outstandingCents = data?.payment?.outstanding_amount_cents || 0;
  const isPaidStatus = ["approved", "waived", "imported_confirmed"].includes(data?.payment?.status);
  const hasOutstanding = outstandingCents > 0;
  const outstandingLabel = `$${(outstandingCents / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN`;

  const addFiles = (files) => {
    setUploadErr(null);
    setUploadMsg(null);
    const incoming = Array.from(files || []);
    if (!incoming.length) return;
    const next = [...queue];
    for (const file of incoming) {
      if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
        setUploadErr(`Formato no permitido en ${file.name}. Usa JPG, PNG o PDF.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setUploadErr(`${file.name} excede el máximo de 5 MB.`);
        continue;
      }
      if (next.length + receivedReceipts.length >= maxReceipts) {
        setUploadErr(`Puedes adjuntar hasta ${maxReceipts} comprobantes por pago.`);
        break;
      }
      next.push(file);
    }
    setQueue(next);
  };

  const removeFromQueue = (idx) => setQueue(queue.filter((_, i) => i !== idx));

  const uploadReceipt = async () => {
    if (!queue.length || uploading) return;
    setUploading(true);
    setUploadErr(null);
    setUploadMsg(null);
    const failed = [];
    let successCount = 0;
    for (const file of queue) {
      try {
        const fd = new FormData();
        if (isGroup) fd.append("group_folio", data.group_folio); else fd.append("folio", data.folio);
        if (uploadEmail) fd.append("email", uploadEmail); else fd.append("phone", uploadPhone);
        fd.append("source", "preregistration");
        fd.append("receipt", file);
        await api.post(isGroup ? "/registrations/group-receipt" : "/registrations/receipt", fd);
        successCount += 1;
      } catch (err) {
        failed.push({ name: file.name, msg: apiError(err, "No se pudo subir.") });
      }
    }
    try {
      const url = isGroup ? `/registrations/groups/${encodeURIComponent(folio)}` : `/registrations/${encodeURIComponent(folio)}`;
      const { data: refreshed } = await api.get(url);
      setData(refreshed);
    } catch { /* best-effort */ }
    setQueue(failed.length ? queue.filter((f) => failed.some((x) => x.name === f.name)) : []);
    if (successCount > 0 && failed.length === 0) {
      setUploadMsg(successCount === 1
        ? "Comprobante recibido. El equipo organizador lo revisará."
        : `${successCount} comprobantes recibidos. El equipo organizador los revisará.`);
    } else if (successCount > 0 && failed.length > 0) {
      setUploadErr(`Se subieron ${successCount} archivo(s). Falló: ${failed.map((f) => `${f.name} (${f.msg})`).join("; ")}`);
    } else {
      setUploadErr(failed[0]?.msg || "No se pudo subir el comprobante. Verifica tu correo o teléfono.");
    }
    setUploading(false);
  };

  useEffect(() => {
    const url = isGroup ? `/registrations/groups/${encodeURIComponent(folio)}` : `/registrations/${encodeURIComponent(folio)}`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e, "No pudimos cargar tu registro.")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folio]);

  const contactName = isGroup ? data?.primary_contact_name : data?.full_name;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-hz-navy" data-testid="confirmacion-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando tu confirmación…
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-hz-error/30 bg-white p-8 text-center" data-testid="confirmacion-error">
            <p className="font-bold text-hz-error">{error}</p>
            <Button onClick={() => navigate("/consulta-registro")} className="mt-6 bg-hz-navy font-bold text-white" data-testid="error-consult-btn">
              Ir a consulta de registro
            </Button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="confirmacion-content">
            <div className="rounded-2xl border border-hz-success/30 bg-white p-8 text-center shadow-sm">
              <div className="mx-auto mb-4 w-fit rounded-2xl border border-hz-gray/40 bg-white p-3">
                <Logo variant="full" className="h-14" />
              </div>
              <CheckCircle2 className="mx-auto h-12 w-12 text-hz-success" />
              <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-hz-navy sm:text-3xl" data-testid="confirmacion-title">
                ¡Pre-registro recibido!
              </h1>
              <p className="mt-2 text-sm text-hz-ink/70">
                Gracias, <span className="font-bold">{contactName}</span>. Tu solicitud fue registrada correctamente.
              </p>
              <div className="mx-auto mt-6 w-fit rounded-2xl border-2 border-hz-cyan/40 bg-hz-cyan/5 px-8 py-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">
                  {isGroup ? (isFamily ? "Folio familiar" : "Folio del grupo") : "Tu folio de pre-registro"}
                </p>
                <p className="mt-1 text-2xl font-extrabold tracking-wider text-hz-navy sm:text-3xl" data-testid="folio-display-number">
                  {isGroup ? data.group_folio : data.folio}
                </p>
              </div>
              {isGroup && (
                <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="member-folios">
                  {data.members.map((m) => (
                    <span key={m.id} className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-bold text-hz-navy">{m.full_name.split(" ")[0]}: {m.folio}</span>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs font-medium text-hz-ink/60">Guarda {isGroup ? (isFamily ? "este folio familiar" : "este folio de grupo") : "este folio"}: lo necesitarás como referencia de pago y para consultar tu registro.</p>
            </div>

            {isGroup ? (
              <GroupSummary data={data} notice="El pago se realiza mediante un solo comprobante por el total del grupo. Si algún integrante pagará por separado, debe registrarse en otro grupo." />
            ) : (
              <RegistrationSummary data={data} />
            )}

            {data.payment?.transfer_accounts != null && (
              <div data-testid="transfer-instructions">
                <TransferAccountsCard accounts={data.payment.transfer_accounts} folio={isGroup ? data.group_folio : data.folio} testId="confirm-transfer-accounts" />
                <p className="mt-2 text-xs leading-relaxed text-hz-ink/70">
                  Tu registro quedará confirmado cuando el equipo organizador revise y apruebe tu comprobante de transferencia.
                </p>
              </div>
            )}

            <div className="rounded-xl border border-hz-gray/40 bg-hz-bg p-4 text-center" data-testid="qr-placeholder">
              <QrCode className="mx-auto h-10 w-10 text-hz-gray" />
              <p className="mt-2 text-sm font-semibold text-hz-ink/70">
                {isGroup
                  ? "Los códigos QR individuales de cada adulto estarán disponibles en «Consultar mi registro» una vez confirmado el pago"
                  : "Tu código QR de acceso estará disponible en «Consultar mi registro» una vez confirmado tu pago"}
              </p>
            </div>

            <div className="rounded-xl border border-hz-gray/40 bg-white p-4 print:hidden" data-testid="receipt-upload-cta">
              {data.payment?.status === "cancelled" ? (
                <p className="text-center text-sm font-semibold text-hz-error" data-testid="confirmacion-cancelled">Este registro fue cancelado. Si crees que es un error, contacta al equipo organizador.</p>
              ) : isPaidStatus && !hasOutstanding ? (
                <>
                  <p className="text-center text-sm font-semibold text-hz-success" data-testid="confirmacion-paid">Pago confirmado.</p>
                  <ReceiptsReceivedList receipts={receivedReceipts} />
                </>
              ) : (
                <div data-testid="confirmacion-upload-card">
                  {hasOutstanding && (
                    <div className="mb-3 rounded-lg border-2 border-hz-warning/50 bg-hz-warning/10 px-3 py-2.5" data-testid="confirmacion-outstanding-banner">
                      <p className="text-sm font-extrabold text-hz-warning">Saldo pendiente: <span data-testid="confirmacion-outstanding-amount">{outstandingLabel}</span></p>
                      <p className="mt-0.5 text-xs font-medium text-hz-ink">
                        {isPaidStatus
                          ? "Tienes accesos aprobados, pero aún queda un saldo por cubrir. Sube el comprobante del pago restante."
                          : "Para completar tus accesos, sube el comprobante del pago restante."}
                      </p>
                    </div>
                  )}
                  <p className="text-sm font-bold text-hz-navy">
                    {hasOutstanding && isPaidStatus
                      ? "Subir comprobante del saldo pendiente"
                      : alreadyInReview ? "¿Necesitas agregar otro comprobante?" : "¿Ya realizaste tu transferencia?"}
                  </p>
                  <p className="mt-1 text-xs text-hz-ink/60">
                    Puedes adjuntar varios comprobantes para respaldar tu pago. JPG, PNG o PDF. Máximo 5 MB por archivo.
                    {remainingSlots < maxReceipts && remainingSlots > 0 && ` (${remainingSlots} espacio${remainingSlots === 1 ? "" : "s"} disponible${remainingSlots === 1 ? "" : "s"})`}
                  </p>
                  {alreadyInReview && (
                    <p className="mt-2 rounded-lg border border-hz-cyan/40 bg-hz-cyan/5 px-3 py-2 text-xs font-semibold text-hz-blue" data-testid="confirmacion-in-review">
                      Comprobante recibido; está en revisión{data.payment?.uploaded_at ? ` desde el ${new Date(data.payment.uploaded_at).toLocaleString("es-MX")}` : ""}. Puedes agregar documentación adicional.
                    </p>
                  )}
                  <ReceiptsReceivedList receipts={receivedReceipts} />
                  {!sessionAuth && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="confirmacion-verify">
                      <Input type="email" placeholder="Correo del registro" value={verify.email} data-testid="confirmacion-verify-email"
                        onChange={(e) => setVerify({ ...verify, email: e.target.value })} />
                      <Input type="tel" placeholder="o Teléfono del registro" value={verify.phone} data-testid="confirmacion-verify-phone"
                        onChange={(e) => setVerify({ ...verify, phone: e.target.value })} />
                    </div>
                  )}
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="confirmacion-camera-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" data-testid="confirmacion-file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => cameraRef.current?.click()} disabled={remainingSlots <= 0} data-testid="confirmacion-camera-btn" className="font-bold">Tomar foto</Button>
                    <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={remainingSlots <= 0} data-testid="confirmacion-pick-btn" className="font-bold">Subir archivos</Button>
                  </div>
                  {queue.length > 0 && (
                    <ul className="mt-3 space-y-1 rounded-lg border border-hz-gray/40 bg-hz-bg p-2 text-xs" data-testid="confirmacion-queue">
                      {queue.map((f, idx) => (
                        <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2" data-testid={`confirmacion-queue-item-${idx}`}>
                          <span className="truncate font-semibold text-hz-ink" title={f.name}>{f.name} <span className="font-normal text-hz-ink/50">({(f.size / 1024).toFixed(0)} KB)</span></span>
                          <button type="button" onClick={() => removeFromQueue(idx)} className="shrink-0 text-xs font-bold text-hz-error hover:underline" data-testid={`confirmacion-queue-remove-${idx}`}>Quitar</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button onClick={uploadReceipt} disabled={!queue.length || uploading || !canAttemptUpload} data-testid="receipt-upload-goto-btn"
                    className="mt-3 w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {alreadyInReview ? "Enviar comprobantes adicionales" : `Subir ${queue.length > 1 ? queue.length + " comprobantes" : "comprobante"}`}
                  </Button>
                  {uploadErr && <p className="mt-2 text-xs font-medium text-hz-error" data-testid="confirmacion-upload-error" role="alert">{uploadErr}</p>}
                  {uploadMsg && <p className="mt-2 text-xs font-semibold text-hz-success" data-testid="confirmacion-upload-success">{uploadMsg}</p>}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row print:hidden">
              <Button onClick={() => navigate("/consulta-registro")} data-testid="consult-registration-btn" className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue">
                <Search className="mr-2 h-4 w-4" /> Consultar mi registro
              </Button>
              <Button variant="outline" onClick={() => window.print()} data-testid="print-btn" className="flex-1 border-hz-navy font-bold text-hz-navy hover:bg-hz-navy hover:text-white">
                <Printer className="mr-2 h-4 w-4" /> Imprimir / Guardar PDF
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} data-testid="back-home-btn" className="flex-1 font-bold text-hz-ink">
                <Home className="mr-2 h-4 w-4" /> Volver al inicio
              </Button>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default ConfirmacionPage;
